import React, { ChangeEvent, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  CardMedia,
  IconButton,
  Input,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import "react-phone-input-2/lib/bootstrap.css";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import PhoneInput from "react-phone-input-2";
import * as Yup from "yup";
import { Autocomplete } from "@react-google-maps/api";
import { useFormik } from "formik";
import {
  useAddUserMutation,
  useLazyGetUserByIdQuery,
  useUpdateUsersMutation,
} from "../../service/users";
import { UploadMedia } from "../../utils/uploadMedia";
import { showError, showToast } from "../../constants/toasts";
import { isValidInput } from "../../utils/validations";
import { keys } from "../../constants/constants";
import usePermission from "../../hooks/usePermission";

// Validation Schema
const validationSchema = Yup.object({
  firstName: Yup.string()
    .required("First Name is required")
    .matches(/^[a-zA-Z\s]*$/, "First name can only contain letters")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be at most 50 characters"),
  lastName: Yup.string()
    .required("Last Name is required")
    .matches(/^[a-zA-Z\s]*$/, "Last name can only contain letters")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be at most 50 characters"),
  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format")
    .max(100, "Email must be at most 100 characters"),
  phoneNumber: Yup.string()
    .required("Phone number is required")
    .matches(/^[0-9]+$/, "Phone number must contain only numbers")
    .min(6, "Phone number must be at least 6 digits")
    .max(15, "Phone number must be at most 15 digits"),
  // address: Yup.string().required("Address is required"),
  address: Yup.object().shape({
    addressLine1: Yup.string().required("Address is required"),
    // city: Yup.string().required("City is required"),
    // state: Yup.string().required("State is required"),
    // country: Yup.string().required("Country is required"),
    // pinCode: Yup.string().required("Pin code is required"),
    // latitude: Yup.number().required("Latitude missing"),
    // longitude: Yup.number().required("Longitude missing"),
  }),
  // city: Yup.string().required("City is required"),
  // country: Yup.string().required("Country is required"),
});

const EditUser = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {} = usePermission({
    label: "Manage Users",
    page_permission: `${id ? "isEdit" : "isAdd"}`,
  });
  const [Status, setStatus] = React.useState("default");
  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value as string);
  };
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);
  const [image, setImage] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const isAddUser = location.pathname === "/manage-users/add";

  // API hooks
  const [getUserById, { isLoading: isLoadingUser }] = useLazyGetUserByIdQuery();
  const [updateUser] = useUpdateUsersMutation();
  const [addUser] = useAddUserMutation();

  const [autocomplete, setAutocomplete] = useState<any>();
  const onLoad = (autocompleteObj: any) => {
    setAutocomplete(autocompleteObj);
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || !files[0]) return;

    const file = files[0];

    // Validate file type
    if (!file.type.includes("image")) {
      setAlertType(0);
      setShowAlert(true);
      setAlertMessage("This field only accepts images.");
      return;
    }

    try {
      // Upload image to your server
      const res = await UploadMedia(file);
      if (res?.code === 200) {
        const imageUrl = res.data;
        setImage(imageUrl);
        formik.setFieldValue("profileImage", imageUrl);
        setFileName(file.name);
        showToast("Image uploaded successfully.");
      } else {
        showError(res?.message || "Failed to upload image");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      showError("Error uploading image. Please try again.");
    }
  };

  console.log(keys.googlePlaces, "keys().googlePlaces");

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      address: {
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        pinCode: "",
        latitude: "",
        longitude: "",
        isDefault: true,
        addressType: "home",
      },
      city: "",
      country: "",
      profileImage: "",
      status: "default",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const userData = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phoneNumber,
          countryCode: "+" + countryCode,
          // address: values.address,
          // city: values.city,
          // country: values.country,
          image: values.profileImage,
          address: {
            ...(id && { userId: id || "" }),
            addressLine1: values.address.addressLine1 || undefined,
            addressLine2: values.address.addressLine2 || undefined,
            city: values.address.city || undefined,
            state: values.address.state || undefined,
            country: values.address.country || undefined,
            pinCode: values.address.pinCode || undefined,
            isDefault: true,
            // addressType: "home",
            latitude: values.address.latitude || undefined,
            longitude: values.address.longitude || undefined,
          },
          // status: values.status,
        };

        let response;

        if (isAddUser) {
          // Add new user
          response = await addUser(userData).unwrap();
          if (response.code === 200) {
            showToast("User added successfully");
            navigate("/manage-users");
          }
        } else {
          // Update existing user
          response = await updateUser({
            id: id as string,
            body: userData,
          }).unwrap();
          if (response.code === 200) {
            showToast("User updated successfully");
            navigate("/manage-users");
          }
        }
      } catch (error: any) {
        console.error("Error saving user:", error);
        showError(error?.data?.message || "Failed to save user");
      }
    },
  });

  // Fetch user data for editing
  useEffect(() => {
    if (!isAddUser && id) {
      fetchUserData();
    }
  }, [id, isAddUser]);

  const onPlaceChanged = async () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    console.log(place, "placeplace");
    if (!place || !place.address_components) return;

    let city = "";
    let state = "";
    let country = "";
    let pinCode = "";

    place.address_components.forEach((component: any) => {
      const types = component.types || [];
      if (
        types.includes("locality") ||
        types.includes("administrative_area_level_2") ||
        types.includes("postal_town")
      ) {
        if (!city) city = component.long_name;
      }
      if (types.includes("administrative_area_level_1"))
        state = component.long_name;
      if (types.includes("country")) country = component.long_name;
      if (types.includes("postal_code")) pinCode = component.long_name;
    });

    const lat = place.geometry?.location?.lat() ?? "";
    const lng = place.geometry?.location?.lng() ?? "";

    formik.setFieldValue("address", {
      ...formik.values.address,
      addressLine1: place.formatted_address || place.name || "",
      city,
      state,
      country,
      pinCode,
      latitude: lat,
      longitude: lng,
    });
  };

  console.log(formik.errors, "formik.errors");

  const fetchUserData = async () => {
    try {
      const response = await getUserById({ id: id as string }).unwrap();
      if (response.code === 200) {
        const user = response.data;

        // Set form values
        formik.setValues({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phoneNumber: user.phone || "",
          address: {
            addressLine1: user.address?.addressLine1 || "",
            addressLine2: user.address?.addressLine2 || "",
            city: user.address?.city || "",
            state: user.address?.state || "",
            country: user.address?.country || "",
            pinCode: user.address?.pinCode || "",
            latitude: user.address?.latitude || "",
            longitude: user.address?.longitude || "",
            isDefault: true,
            addressType: "home",
          },
          city: user.city || "",
          country: user.country || "",
          profileImage: user.image || "",
          status: user.status || "default",
        });

        // Set image if exists
        if (user.image) {
          setImage(user.image);
        }

        // Set phone display
        if (user.countryCode && user.phone) {
          setPhoneDisplay(`${user.countryCode}${user.phone}`);
          setCountryCode(user.countryCode.replaceAll("+", ""));
        }
      }
    } catch (error: any) {
      console.error("Error fetching user data:", error);
      showError(error?.data?.message || "Failed to fetch user data");
      navigate("/manage-users");
    }
  };

  const handleClear = () => {
    formik.setFieldValue("address", {
      ...formik.values.address,
      addressLine1: "",
      city: "",
      state: "",
      country: "",
      pinCode: "",
      latitude: "",
      longitude: "",
    });
  };

  const handlePhoneChange = (phone: string, country: any) => {
    const phoneWithoutCode = phone.replace(country.dialCode, "");
    formik.setFieldValue("phoneNumber", phoneWithoutCode);
    setCountryCode(country.dialCode);
    setPhoneDisplay(phone);
  };

  console.log(formik.values.address, "formik.values.address");

  return (
    <>
      <div className="main_title">
        <h1>{isAddUser ? "Add " : "Edit "} User</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-users")}>
            Manage Users
          </button>{" "}
          - {isAddUser ? "Add " : "Edit "} User
        </p>
      </div>

      <div className="cards">
        <div className="form">
          <form onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_100">
                <label htmlFor="icon-button-file">Profile Picture</label>
                {image ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={image} alt="photo" />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          setImage("");
                          setFileName("");
                          formik.setFieldValue("profileImage", "");
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{
                        accept: "image/png,image/jpeg",
                      }}
                      onChange={handleImageUpload}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/user_placeholder.png"
                          alt="icon"
                        />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
              </div>
              <div className="control_group w_33">
                <label htmlFor="firstName">First Name</label>
                <TextField
                  id="firstName"
                  name="firstName"
                  placeholder="First Name"
                  hiddenLabel
                  fullWidth
                  value={formik.values.firstName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length < 40) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="lastName">Last Name</label>
                <TextField
                  id="lastName"
                  name="lastName"
                  placeholder="Last Name"
                  hiddenLabel
                  fullWidth
                  value={formik.values.lastName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length < 40) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.lastName && Boolean(formik.errors.lastName)
                  }
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </div>

              <div className="control_group w_33">
                <label htmlFor="email">Email Address</label>
                <TextField
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  hiddenLabel
                  fullWidth
                  value={formik.values.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (isValidInput(value) && value.length < 40) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="phone">Phone Number</label>
                <PhoneInput
                  country={"in"}
                  placeholder="Enter Phone Number"
                  enableSearch={false}
                  autoFormat={false}
                  inputStyle={{ width: "100%" }}
                  value={phoneDisplay}
                  onChange={handlePhoneChange}
                />
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {formik.errors.phoneNumber}
                  </Typography>
                )}
              </div>
              <div className="control_group w_33">
                <label htmlFor="address">Address</label>
                <Autocomplete
                  onLoad={onLoad}
                  onPlaceChanged={onPlaceChanged}
                  options={{
                    fields: [
                      "address_components",
                      "geometry",
                      "formatted_address",
                      "name",
                    ],
                  }}
                >
                  <TextField
                    className="text_field add_cross"
                    hiddenLabel
                    name="address.addressLine1"
                    value={formik.values.address.addressLine1}
                    placeholder="Enter Address"
                    fullWidth
                    onChange={(e) => {
                      if (e.target.value.trim() === "") {
                        formik.setFieldValue("address.addressLine1", "");
                      } else {
                        formik.handleChange(e);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.address?.addressLine1 &&
                      Boolean(formik.errors.address?.addressLine1)
                    }
                    helperText={
                      formik.touched.address?.addressLine1 &&
                      formik.errors.address?.addressLine1
                    }
                    InputProps={{
                      endAdornment: formik.values.address.addressLine1 ? (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClear}>
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Autocomplete>

                {/* <Autocomplete
                  onLoad={onLoad}
                  onPlaceChanged={() => onPlaceChanged()}
                >
                  <TextField
                    className="text_field add_cross"
                    hiddenLabel
                    name="address.addressLine1"
                    value={formik.values.address.addressLine1}
                    placeholder="Enter Address"
                    fullWidth
                    onChange={(e) => {
                      if (e.target.value.trim() === "") {
                        formik.setFieldValue("address.addressLine1", "");
                      } else {
                        formik.handleChange(e);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                    InputProps={{
                      endAdornment: formik.values.address.addressLine1 ? (
                        <InputAdornment position="end">
                          <IconButton onClick={handleClear}>
                            <CloseIcon />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Autocomplete> */}
                {/* <TextField
                  id="address"
                  name="address"
                  placeholder="Address"
                  hiddenLabel
                  fullWidth
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.address && Boolean(formik.errors.address)
                  }
                  helperText={formik.touched.address && formik.errors.address}
                /> */}
              </div>
              <div className="control_group w_33">
                <label htmlFor="city">City</label>
                <TextField
                  id="city"
                  name="address.city"
                  placeholder="City"
                  hiddenLabel
                  fullWidth
                  value={formik.values.address.city}
                  disabled
                  // error={formik.touched.address.city && Boolean(formik.errors.address.city)}
                  // helperText={formik.touched.address.city && formik.errors.address.city}
                />
              </div>
              <div className="control_group w_33">
                <label htmlFor="country">Country</label>
                <TextField
                  id="country"
                  name="address.country"
                  placeholder="Country"
                  hiddenLabel
                  fullWidth
                  value={formik.values.address.country}
                  disabled
                  // error={formik.touched.address.country && Boolean(formik.errors.address.country)}
                  // helperText={formik.touched.address.country && formik.errors.address.country}
                />
              </div>
            </div>

            <div className="form_btn">
              <Button className="btn btn_primary" type="submit">
                Save
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditUser;

// import React, { ChangeEvent, useEffect, useState } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import {
//   Button,
//   CardMedia,
//   IconButton,
//   Input,
//   InputAdornment,
//   MenuItem,
//   Select,
//   SelectChangeEvent,
//   TextField,
//   Typography,
// } from "@mui/material";
// import "react-phone-input-2/lib/bootstrap.css";
// import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
// import CloseIcon from "@mui/icons-material/Close";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import PhoneInput from "react-phone-input-2";
// import * as Yup from "yup";
// import { Autocomplete } from "@react-google-maps/api";
// import { useFormik } from "formik";
// import {
//   useAddUserMutation,
//   useLazyGetUserByIdQuery,
//   useUpdateUsersMutation,
// } from "../../service/users";
// import { UploadMedia } from "../../utils/uploadMedia";
// import { showError, showToast } from "../../constants/toasts";
// import { isValidInput } from "../../utils/validations";
// import { keys } from "../../constants/constants";

// // Validation Schema
// const validationSchema = Yup.object({
//   firstName: Yup.string()
//     .required("First Name is required")
//     .matches(/^[a-zA-Z\s]*$/, "First name can only contain letters")
//     .min(2, "First name must be at least 2 characters")
//     .max(50, "First name must be at most 50 characters"),
//   lastName: Yup.string()
//     .required("Last Name is required")
//     .matches(/^[a-zA-Z\s]*$/, "Last name can only contain letters")
//     .min(2, "Last name must be at least 2 characters")
//     .max(50, "Last name must be at most 50 characters"),
//   email: Yup.string()
//     .required("Email is required")
//     .email("Invalid email format")
//     .max(100, "Email must be at most 100 characters"),
//   phoneNumber: Yup.string()
//     .required("Phone number is required")
//     .matches(/^[0-9]+$/, "Phone number must contain only numbers")
//     .min(6, "Phone number must be at least 6 digits")
//     .max(15, "Phone number must be at most 15 digits"),
//   address: Yup.string().required("Address is required"),
//   city: Yup.string().required("City is required"),
//   country: Yup.string().required("Country is required"),
// });

// const EditUser = () => {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { id } = useParams();
//   const [Status, setStatus] = React.useState("default");
//   const handleStatusChange = (event: SelectChangeEvent) => {
//     setStatus(event.target.value as string);
//   };
//   const [alertMessage, setAlertMessage] = useState("");
//   const [showAlert, setShowAlert] = useState(false);
//   const [alertType, setAlertType] = useState(0);
//   const [image, setImage] = useState<string>("");
//   const [fileName, setFileName] = useState<string>("");
//   const [phoneDisplay, setPhoneDisplay] = useState("");
//   const [countryCode, setCountryCode] = useState("+91");
//   const isAddUser = location.pathname === "/manage-users/add";

//   // API hooks
//   const [getUserById, { isLoading: isLoadingUser }] = useLazyGetUserByIdQuery();
//   const [updateUser] = useUpdateUsersMutation();
//   const [addUser] = useAddUserMutation();

//   const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
//     const files = event.target.files;
//     if (!files || !files[0]) return;

//     const file = files[0];

//     // Validate file type
//     if (!file.type.includes("image")) {
//       setAlertType(0);
//       setShowAlert(true);
//       setAlertMessage("This field only accepts images.");
//       return;
//     }

//     try {
//       // Upload image to your server
//       const res = await UploadMedia(file);
//       if (res?.code === 200) {
//         const imageUrl = res.data;
//         setImage(imageUrl);
//         formik.setFieldValue("profileImage", imageUrl);
//         setFileName(file.name);
//         showToast("Image uploaded successfully.");
//       } else {
//         showError(res?.message || "Failed to upload image");
//       }
//     } catch (error) {
//       console.error("Error uploading image:", error);
//       showError("Error uploading image. Please try again.");
//     }
//   };

//   console.log(keys.googlePlaces, "keys().googlePlaces");

//   const formik = useFormik({
//     initialValues: {
//       firstName: "",
//       lastName: "",
//       email: "",
//       phoneNumber: "",
//       address: "",
//       city: "",
//       country: "",
//       profileImage: "",
//       status: "default",
//     },
//     validationSchema,
//     onSubmit: async (values) => {
//       try {
//         const userData = {
//           firstName: values.firstName,
//           lastName: values.lastName,
//           email: values.email,
//           phone: values.phoneNumber,
//           countryCode: "+" + countryCode,
//           address: {
//             addressLine1: values.address,
//             city: values.city,
//             country: values.country,
//           },
//           // address: values.address,
//           // city: values.city,
//           // country: values.country,
//           image: values.profileImage,
//           // status: values.status,
//         };

//         let response;

//         if (isAddUser) {
//           // Add new user
//           response = await addUser(userData).unwrap();
//           if (response.code === 200) {
//             showToast("User added successfully");
//             navigate("/manage-users");
//           }
//         } else {
//           // Update existing user
//           response = await updateUser({
//             id: id as string,
//             body: userData,
//           }).unwrap();
//           if (response.code === 200) {
//             showToast("User updated successfully");
//             navigate("/manage-users");
//           }
//         }
//       } catch (error: any) {
//         console.error("Error saving user:", error);
//         showError(error?.data?.message || "Failed to save user");
//       }
//     },
//   });

//   // Fetch user data for editing
//   useEffect(() => {
//     if (!isAddUser && id) {
//       fetchUserData();
//     }
//   }, [id, isAddUser]);

//   const fetchUserData = async () => {
//     try {
//       const response = await getUserById({ id: id as string }).unwrap();
//       if (response.code === 200) {
//         const user = response.data;

//         // Set form values
//         formik.setValues({
//           firstName: user.firstName || "",
//           lastName: user.lastName || "",
//           email: user.email || "",
//           phoneNumber: user.phone || "",
//           address: user?.address?.addressLine1 || "",
//           city: user?.address?.city || "",
//           country: user?.address?.country || "",
//           profileImage: user.image || "",
//           status: user.status || "default",
//         });

//         // Set image if exists
//         if (user.image) {
//           setImage(user.image);
//         }

//         // Set phone display
//         if (user.countryCode && user.phone) {
//           setPhoneDisplay(`${user.countryCode}${user.phone}`);
//           setCountryCode(user.countryCode.replaceAll("+", ""));
//         }
//       }
//     } catch (error: any) {
//       console.error("Error fetching user data:", error);
//       showError(error?.data?.message || "Failed to fetch user data");
//       navigate("/manage-users");
//     }
//   };

//   const handleClear = () => {
//     // formik.setFieldValue("latitude", 0);
//     // formik.setFieldValue("longitude", 0);
//     formik.setFieldValue("address", "");
//   };

//   const handlePhoneChange = (phone: string, country: any) => {
//     const phoneWithoutCode = phone.replace(country.dialCode, "");
//     formik.setFieldValue("phoneNumber", phoneWithoutCode);
//     setCountryCode(country.dialCode);
//     setPhoneDisplay(phone);
//   };

//   return (
//     <>
//       <div className="main_title">
//         <h1>{isAddUser ? "Add " : "Edit "} User</h1>
//         <p>
//           <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
//           <button onClick={() => navigate("/manage-users")}>
//             Manage Users
//           </button>{" "}
//           - {isAddUser ? "Add " : "Edit "} User
//         </p>
//       </div>

//       <div className="cards">
//         <div className="form">
//           <form onSubmit={formik.handleSubmit}>
//             <div className="gap_p">
//               <div className="control_group w_100">
//                 <label htmlFor="icon-button-file">Profile Picture</label>
//                 {image ? (
//                   <div className="upload_image">
//                     <div className="upload_image_holder">
//                       <figure>
//                         <CardMedia component="img" image={image} alt="photo" />
//                       </figure>
//                       <CloseIcon
//                         onClick={() => {
//                           setImage("");
//                           setFileName("");
//                           formik.setFieldValue("profileImage", "");
//                         }}
//                       />
//                     </div>
//                   </div>
//                 ) : (
//                   <label className="upload_image" htmlFor="icon-button-file">
//                     <Input
//                       sx={{ display: "none" }}
//                       id="icon-button-file"
//                       type="file"
//                       inputProps={{
//                         accept: "image/png,image/jpeg",
//                       }}
//                       onChange={handleImageUpload}
//                     />
//                     <span className="upload_image_holder">
//                       <figure className="add_img">
//                         <img
//                           src="/static/images/user_placeholder.png"
//                           alt="icon"
//                         />
//                         <span className="upload_img_icon">
//                           <img src="/static/images/edit2_icon.svg" alt="icon" />
//                         </span>
//                       </figure>
//                     </span>
//                   </label>
//                 )}
//               </div>
//               <div className="control_group w_33">
//                 <label htmlFor="firstName">First Name</label>
//                 <TextField
//                   id="firstName"
//                   name="firstName"
//                   placeholder="First Name"
//                   hiddenLabel
//                   fullWidth
//                   value={formik.values.firstName}
//                   onChange={(e) => {
//                     const value = e.target.value;
//                     if (isValidInput(value) && value.length < 40) {
//                       formik.handleChange(e);
//                     }
//                   }}
//                   onBlur={formik.handleBlur}
//                   error={
//                     formik.touched.firstName && Boolean(formik.errors.firstName)
//                   }
//                   helperText={
//                     formik.touched.firstName && formik.errors.firstName
//                   }
//                 />
//               </div>
//               <div className="control_group w_33">
//                 <label htmlFor="lastName">Last Name</label>
//                 <TextField
//                   id="lastName"
//                   name="lastName"
//                   placeholder="Last Name"
//                   hiddenLabel
//                   fullWidth
//                   value={formik.values.lastName}
//                   onChange={(e) => {
//                     const value = e.target.value;
//                     if (isValidInput(value) && value.length < 40) {
//                       formik.handleChange(e);
//                     }
//                   }}
//                   onBlur={formik.handleBlur}
//                   error={
//                     formik.touched.lastName && Boolean(formik.errors.lastName)
//                   }
//                   helperText={formik.touched.lastName && formik.errors.lastName}
//                 />
//               </div>

//               <div className="control_group w_33">
//                 <label htmlFor="email">Email Address</label>
//                 <TextField
//                   id="email"
//                   name="email"
//                   type="email"
//                   placeholder="Email Address"
//                   hiddenLabel
//                   fullWidth
//                   value={formik.values.email}
//                   onChange={(e) => {
//                     const value = e.target.value;
//                     if (isValidInput(value) && value.length < 40) {
//                       formik.handleChange(e);
//                     }
//                   }}
//                   onBlur={formik.handleBlur}
//                   error={formik.touched.email && Boolean(formik.errors.email)}
//                   helperText={formik.touched.email && formik.errors.email}
//                 />
//               </div>
//               <div className="control_group w_33">
//                 <label htmlFor="phone">Phone Number</label>
//                 <PhoneInput
//                   country={"in"}
//                   placeholder="Enter Phone Number"
//                   enableSearch={false}
//                   autoFormat={false}
//                   inputStyle={{ width: "100%" }}
//                   value={phoneDisplay}
//                   onChange={handlePhoneChange}
//                 />
//                 {formik.touched.phoneNumber && formik.errors.phoneNumber && (
//                   <Typography
//                     variant="caption"
//                     color="error"
//                     sx={{ display: "block", mt: 0.5 }}
//                   >
//                     {formik.errors.phoneNumber}
//                   </Typography>
//                 )}
//               </div>
//               <div className="control_group w_33">
//                 <label htmlFor="address">Address</label>

//                 <TextField
//                   id="address"
//                   name="address"
//                   placeholder="Address"
//                   hiddenLabel
//                   fullWidth
//                   value={formik.values.address}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                   error={
//                     formik.touched.address && Boolean(formik.errors.address)
//                   }
//                   helperText={formik.touched.address && formik.errors.address}
//                 />
//               </div>
//               <div className="control_group w_33">
//                 <label htmlFor="city">City</label>
//                 <TextField
//                   id="city"
//                   name="city"
//                   placeholder="City"
//                   hiddenLabel
//                   fullWidth
//                   value={formik.values.city}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                   error={formik.touched.city && Boolean(formik.errors.city)}
//                   helperText={formik.touched.city && formik.errors.city}
//                 />
//               </div>
//               <div className="control_group w_33">
//                 <label htmlFor="country">Country</label>
//                 <TextField
//                   id="country"
//                   name="country"
//                   placeholder="Country"
//                   hiddenLabel
//                   fullWidth
//                   value={formik.values.country}
//                   onChange={formik.handleChange}
//                   onBlur={formik.handleBlur}
//                   error={
//                     formik.touched.country && Boolean(formik.errors.country)
//                   }
//                   helperText={formik.touched.country && formik.errors.country}
//                 />
//               </div>
//             </div>

//             <div className="form_btn">
//               <Button className="btn btn_primary" type="submit">
//                 Save
//               </Button>
//             </div>
//           </form>
//         </div>
//       </div>
//     </>
//   );
// };

// export default EditUser;
